import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    PublishedWorkShiftDto,
    PublishedWorkShiftQueryParameters,
} from "../models/publishedWorkShift/publishedWorkShift";
import { getPublishedWorkShifts } from "../services/publishedWorkShiftService";
import { Base } from "../framework/base";

interface WorkShiftPublishedState {
    publishedWorkShifts: PublishedWorkShiftDto[];
    shiftsLoading: boolean;
}

export const initialState: WorkShiftPublishedState = {
    publishedWorkShifts: [],
    shiftsLoading: false,
};

export const workShiftPublishedSlice = createSlice({
    name: "workShiftPublished",
    initialState: initialState,
    reducers: {
        setPublishedWorkShifts: (
            state,
            action: PayloadAction<PublishedWorkShiftDto[]>
        ) => {
            state.publishedWorkShifts = action.payload;
        },
        setPublishedWorkShiftsLoading: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.shiftsLoading = action.payload;
        }
    },
});

export const fetchPublishedWorkShifts = createAsyncThunk(
    "workShiftPublished/list",
    async(params: PublishedWorkShiftQueryParameters, thunkApi) => {
        try {
            thunkApi.dispatch(setPublishedWorkShiftsLoading(true));
            const shifts = await getPublishedWorkShifts(params, thunkApi.signal);
            thunkApi.dispatch(setPublishedWorkShifts(shifts));
        } finally {
            thunkApi.dispatch(setPublishedWorkShiftsLoading(false));
        }
    }
);

export const {
    setPublishedWorkShifts,
    setPublishedWorkShiftsLoading,
} = workShiftPublishedSlice.actions;

export const workShiftPublishedReducer = workShiftPublishedSlice.reducer;

export const makeSelectPublishedWorkShiftsByDate = () =>
    createSelector(
        (state: WorkShiftPublishedState) => state.publishedWorkShifts,
        (_state, rangeStart: string) => rangeStart,
        (_state, _rangeStart: string, rangeEnd: string) => rangeEnd,
        (_state, _rangeStart: string, _rangeEnd: string, employeeId: string) => employeeId,
        (
            items: PublishedWorkShiftDto[],
            rangeStart: string,
            rangeEnd: string,
            employeeId: string
        ) => {
            if (rangeStart && rangeEnd) {
                let filtered = items?.filter((i) =>
                    Base.isBetween(
                        i.effectiveDate,
                        rangeStart,
                        rangeEnd,
                        "day",
                        "[]"
                    )
                );
                if (employeeId) {
                    filtered = filtered?.filter((i) => i.employeeId === employeeId);
                }
                return filtered;
            }
        }
    );

export const makeSelectPublishedWorkShiftsDurationByDate = () =>
    createSelector([makeSelectPublishedWorkShiftsByDate()], (items) =>
        items ? items.reduce((acc, cur) => acc + (cur.duration ?? 0), 0) : undefined
    );
